/**
 * Team Configuration Provider — default single-team instance provider
 * Simplified single-team provider. No sport-switching needed.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { getClientDisplayName } from '../../utils/clientBranding';
import { creightonConfig, defaultConfig } from './teams';

const TeamContext = createContext(null);

export function TeamProvider({ children }) {
  const value = useMemo(() => ({
    teamId: 'creighton',
    config: creightonConfig,
    colors: creightonConfig.colors,
    branding: creightonConfig.branding,
    features: creightonConfig.features,
    analysis: creightonConfig.analysis,
    logos: creightonConfig.logos,
    sportCode: creightonConfig.sportCode,
    isLoading: false,
  }), []);

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const ctx = useContext(TeamContext);
  if (!ctx) return { config: defaultConfig, teamId: 'creighton', isLoading: false };
  return ctx;
}

export function useTeamConfig() {
  const { config } = useTeam();
  return config;
}

export function useTeamColors() {
  const { colors } = useTeam();
  return colors || creightonConfig.colors;
}

export function useTeamBranding() {
  const { branding } = useTeam();
  return branding || creightonConfig.branding;
}

export function useTeamFeatures() {
  const { features } = useTeam();
  return features || creightonConfig.features;
}

export function useFeatureEnabled(featurePath) {
  const features = useTeamFeatures();
  const parts = featurePath.split('.');
  let value = features;
  for (const part of parts) {
    value = value?.[part];
  }
  return Boolean(value);
}

export function useSelectedTeam() {
  return { teamId: 'creighton', teamName: getClientDisplayName() };
}

export function useSportInfo() {
  return { sportCode: 'MBB', sportName: "Men's College Basketball", category: 'basketball' };
}

export function useTeamAnalysis() {
  const { analysis } = useTeam();
  return analysis || creightonConfig.analysis;
}

export function useTeamConfigForAPI() {
  return {
    teamId: 'creighton',
    sportCode: 'MBB',
    systemPromptAdditions: creightonConfig.analysis?.systemPromptAdditions || '',
  };
}
